import type { ScanDataMutators } from '@kaiizen/kai-pro-api-react-state';
import {
  getScanTemplateTask,
  getScanWithUpdatedGroup,
  getScanWithUpdatedLineItem,
  getScopeForScanPartChildren,
  initTaskFromTemplate,
  isLineItemScoped,
  ScanLineItem,
  ScanRootOptimized,
  ScanTemplateLineItem,
  ScanTemplateOption,
} from '@kaiizen/kai-scan-template-system';

import {
  applyVoiceScopeSetTaskValue,
  markVoiceScopeTaskApplied,
  requireVoiceScopeGroup,
  requireVoiceScopeTask,
  stampVoiceScopeAgentProvenanceOnAddedOption,
  type VoiceScopeAgentProvenance,
  voiceScopeLineItemAppliedUpdates,
  voiceScopeTaskAppliedUpdates,
} from './applyVoiceScopeCommand';
import type { VoiceScopeAddOptionCommand, VoiceScopeCommand } from './voiceScopeTypes';

type VoiceScopeMutators = Pick<ScanDataMutators, 'setScan' | 'addOption' | 'addLineItem' | 'updateTask'>;
type AddOptionCommand = VoiceScopeAddOptionCommand;

const DEFAULT_ADD_QUANTITY = 1;
const LINE_ITEM_QUANTITY = 'quantity';

const lineItemTemplateFor = (optionTemplate: ScanTemplateOption, command: AddOptionCommand) => {
  const templates = optionTemplate.lineItemTemplates ?? [];
  if (!command.lineItemTemplateId) return templates[0];
  return templates.find(lineItem => lineItem.id === command.lineItemTemplateId);
};

const spokenPriceKey = (lineItemTemplate: ScanTemplateLineItem) => {
  const values = lineItemTemplate.values ?? {};
  if (values.laborPrice) return 'laborPrice';
  if (values.price && (lineItemTemplate.skuIdOptions?.length ?? 0) === 0) return 'price';
  return undefined;
};

const lineItemInitializers = (
  lineItemTemplate: ScanTemplateLineItem,
  { quantity, spokenPrice }: { quantity?: number; spokenPrice?: number },
): Partial<ScanLineItem> | undefined => {
  const values = lineItemTemplate.values ?? {};
  const quantityKey = values[LINE_ITEM_QUANTITY] ? LINE_ITEM_QUANTITY : Object.keys(values)[0];
  const priceKey = spokenPrice === undefined ? undefined : spokenPriceKey(lineItemTemplate);
  if (!quantityKey && !priceKey) return undefined;
  return {
    values: {
      ...(quantityKey ? { [quantityKey]: { setValue: quantity ?? DEFAULT_ADD_QUANTITY } } : {}),
      ...(priceKey ? { [priceKey]: { setValue: spokenPrice } } : {}),
    },
  };
};

const mergeLineItemInitializers = (
  initializers: Partial<ScanLineItem> | undefined,
  provenance: VoiceScopeAgentProvenance,
): Partial<ScanLineItem> => ({
  ...initializers,
  agentUpdatedAt: provenance.agentUpdatedAt,
  agentUpdateAcceptedAt: provenance.agentUpdateAcceptedAt,
});

const ensureVoiceScopeTask = (
  scan: ScanRootOptimized,
  groupId: string,
  command: AddOptionCommand,
  provenance: VoiceScopeAgentProvenance,
): ScanRootOptimized => {
  const group = requireVoiceScopeGroup(scan, groupId);
  if (group.tasksById[command.taskId]) return scan;
  const taskTemplate = getScanTemplateTask(scan.template, group.template, command.taskTemplateId);
  const task = {
    ...initTaskFromTemplate(taskTemplate, getScopeForScanPartChildren(group)),
    id: command.taskId,
    ...voiceScopeTaskAppliedUpdates(provenance),
  };
  return getScanWithUpdatedGroup(scan, groupId, { tasks: [...group.tasks, task] });
};

/** Runs voice scope commands through the same scan data mutators the editor uses. */
export const createVoiceScopeCommandRunner = (
  scanDataMutators: VoiceScopeMutators,
  currentScan: () => ScanRootOptimized | undefined,
) => {
  const addOption = async (
    command: AddOptionCommand,
    provenance: VoiceScopeAgentProvenance,
  ): Promise<ScanRootOptimized> => {
    const { groupId } = command;
    const scan = currentScan()?.groupsById[groupId]?.tasksById[command.taskId]
      ? currentScan()!
      : await scanDataMutators.setScan(current => ensureVoiceScopeTask(current, groupId, command, provenance));
    const task = requireVoiceScopeTask(requireVoiceScopeGroup(scan, groupId), command.taskId);
    const optionTemplate = task.template.optionTemplates?.find(option => option.id === command.optionTemplateId);
    if (!optionTemplate) throw new Error(`${command.taskName} has no option ${command.optionName}`);
    const lineItemTemplate = lineItemTemplateFor(optionTemplate, command);
    const initializers = lineItemTemplate && lineItemInitializers(lineItemTemplate, command);
    const initializersWithProvenance = mergeLineItemInitializers(initializers, provenance);

    const existingOption = task.options.find(option => option.template.id === command.optionTemplateId);
    if (!existingOption) {
      await scanDataMutators.addOption({
        groupId,
        taskId: command.taskId,
        optionTemplateId: command.optionTemplateId,
        addLineItemTemplateId: lineItemTemplate?.id,
        addLineItemInitializers: initializersWithProvenance,
      });
      return scanDataMutators.setScan(current =>
        markVoiceScopeTaskApplied(
          stampVoiceScopeAgentProvenanceOnAddedOption(
            current,
            groupId,
            command.taskId,
            command.optionTemplateId,
            provenance,
          ),
          groupId,
          command.taskId,
          provenance,
        )
      );
    }

    const existingLineItem = lineItemTemplate
      && existingOption.lineItems.find(lineItem => lineItem.template.id === lineItemTemplate.id);
    if (!lineItemTemplate) {
      throw new Error(`${command.optionName} has no line item to add`);
    }
    if (existingLineItem) {
      if (isLineItemScoped(existingLineItem)) {
        throw new Error(`${command.optionName} is already in scope`);
      }
      if (!initializers) {
        throw new Error(`${command.optionName} is already present but has nothing to update`);
      }
      return scanDataMutators.setScan(current =>
        markVoiceScopeTaskApplied(
          getScanWithUpdatedLineItem(
            current,
            groupId,
            command.taskId,
            existingOption.id,
            existingLineItem.id,
            voiceScopeLineItemAppliedUpdates(existingLineItem, initializers, provenance),
          ),
          groupId,
          command.taskId,
          provenance,
        )
      );
    }

    await scanDataMutators.addLineItem({
      groupId,
      taskId: command.taskId,
      optionId: existingOption.id,
      lineItemTemplateId: lineItemTemplate.id,
      initializers: initializersWithProvenance,
    });
    return scanDataMutators.setScan(current =>
      markVoiceScopeTaskApplied(
        stampVoiceScopeAgentProvenanceOnAddedOption(
          current,
          groupId,
          command.taskId,
          command.optionTemplateId,
          provenance,
        ),
        groupId,
        command.taskId,
        provenance,
      )
    );
  };

  return async (
    command: VoiceScopeCommand,
    provenance: VoiceScopeAgentProvenance,
  ): Promise<ScanRootOptimized> => {
    if (command.type === 'addOption') return addOption(command, provenance);
    return scanDataMutators.setScan(current =>
      markVoiceScopeTaskApplied(
        applyVoiceScopeSetTaskValue(current, command.groupId, command, provenance),
        command.groupId,
        command.taskId,
        provenance,
      )
    );
  };
};
